import React, { useCallback, useEffect, useMemo, useState } from "react";
import type { Contract } from "@meupedaco/shared";
import { CrudPage } from "../../components/CrudPage";
import { useAuth } from "../../auth/AuthContext";
import { formatDateBR, formatMoneyBR } from "../../utils/format";

type ContractWithSupplier = Contract & {
  supplier?: { id: number; entity: { name: string } } | null;
};

const toDateInput = (value: string | number | boolean | null) => {
  if (!value) return "";
  const normalized =
    typeof value === "string" ? value : new Date(value).toISOString();
  return normalized.slice(0, 10);
};

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  ONCE: "Único",
  MONTHLY: "Mensal",
  YEARLY: "Anual",
};

const PAYMENT_TYPE_COLORS: Record<string, string> = {
  ONCE: "text-bg-info",
  MONTHLY: "text-bg-primary",
  YEARLY: "text-bg-secondary",
};

const INDEX_LABELS: Record<string, string> = {
  NONE: "Sem índice",
  IPCA: "IPCA",
  IGPM: "IGPM",
};

// ─── Duration calculator + generate payables ─────────────────────────────────
const ContractFormExtras: React.FC<{
  values: Record<string, string | number | boolean | null>;
  setValues: React.Dispatch<React.SetStateAction<Record<string, string | number | boolean | null>>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  editingId: number | null;
  readOnly: boolean;
  refresh: () => Promise<void>;
  onGenerate: (contractId: number) => Promise<boolean>;
}> = ({ values, setValues, setError, editingId, readOnly, refresh, onGenerate }) => {
  const [contractMonths, setContractMonths] = useState("12");

  useEffect(() => {
    if (!editingId) return;
    const normalizedStart = toDateInput(values.startDate);
    const normalizedEnd = toDateInput(values.endDate);
    if (normalizedStart && normalizedStart !== values.startDate)
      setValues((prev) => ({ ...prev, startDate: normalizedStart }));
    if (normalizedEnd && normalizedEnd !== values.endDate)
      setValues((prev) => ({ ...prev, endDate: normalizedEnd }));
  }, [editingId, setValues, values.endDate, values.startDate]);

  const calculateEndDate = () => {
    if (!values.startDate) { setError("Selecione a data de início primeiro."); return; }
    const start = new Date(`${toDateInput(values.startDate)}T00:00:00`);
    const end = new Date(start);
    end.setMonth(end.getMonth() + Number(contractMonths));
    setValues({ ...values, endDate: end.toISOString().split("T")[0] });
  };

  return (
    <div>
      <div className="mb-3">
        <label className="form-label fw-semibold">Duração do contrato</label>
        <div className="d-flex gap-2 align-items-center">
          <select
            className="form-select"
            style={{ width: 140 }}
            value={contractMonths}
            onChange={(e) => setContractMonths(e.target.value)}
            disabled={readOnly}
          >
            {[12, 24, 36, 48, 60].map((m) => (
              <option key={m} value={String(m)}>{m} meses</option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={calculateEndDate}
            disabled={readOnly || !values.startDate}
          >
            Calcular término
          </button>
        </div>
        <small className="text-muted">Preenche automaticamente a data de término</small>
      </div>

      <div className="mb-3">
        <button
          type="button"
          className="btn btn-outline-success btn-sm"
          disabled={!editingId || readOnly}
          onClick={async () => {
            if (!editingId) { setError("Salve o contrato primeiro."); return; }
            const ok = await onGenerate(editingId);
            if (!ok) { setError("Falha ao gerar contas."); return; }
            await refresh();
          }}
        >
          ⚡ Gerar contas a pagar
        </button>
      </div>
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────
export const ContractsPage: React.FC = () => {
  const { client } = useAuth();
  const [activeFilter, setActiveFilter] = useState("active");

  const listFilters = useMemo(() => {
    if (activeFilter === "all") return {};
    return { isActive: activeFilter === "active" };
  }, [activeFilter]);

  const fetchSuppliers = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/suppliers?q=${encodeURIComponent(query)}&take=20&skip=0`
      );
      if (!response.ok) return [];
      const payload = (await response.json()) as {
        data: Array<{ id: number; entity: { name: string } }>;
      };
      return payload.data.map((item) => ({ id: item.id, label: item.entity.name }));
    },
    [client]
  );

  const filterBar = (
    <div
      className="d-flex flex-wrap align-items-center gap-2 px-3 py-2 mb-2 rounded"
      style={{ background: "#f8f9fa", borderTop: "3px solid #0d6efd" }}
    >
      <small className="text-muted fw-semibold me-1">Filtros</small>
      <select
        className="form-select form-select-sm"
        style={{ width: 180 }}
        value={activeFilter}
        onChange={(e) => setActiveFilter(e.target.value)}
      >
        <option value="active">Somente ativos</option>
        <option value="inactive">Somente inativos</option>
        <option value="all">Todos</option>
      </select>
    </div>
  );

  return (
    <CrudPage<ContractWithSupplier>
      title="Contratos"
      endpoint="/modules/contracts"
      searchPlaceholder="Buscar por título..."
      listFilters={listFilters}
      renderFilters={filterBar}
      columns={[
        {
          label: "Título / Fornecedor",
          render: (item) => (
            <div>
              <div className="fw-semibold">{item.title}</div>
              {item.supplier?.entity?.name && (
                <div className="text-muted small">{item.supplier.entity.name}</div>
              )}
            </div>
          ),
        },
        {
          label: "Vigência",
          render: (item) => (
            <div>
              <div className="small">{formatDateBR(item.startDate)}</div>
              <div className="text-muted small">até {formatDateBR(item.endDate)}</div>
            </div>
          ),
        },
        {
          label: "Tipo",
          render: (item) => (
            <span
              className={`badge ${PAYMENT_TYPE_COLORS[item.paymentType] ?? "text-bg-secondary"}`}
              style={{ fontSize: "0.7rem" }}
            >
              {PAYMENT_TYPE_LABELS[item.paymentType] ?? item.paymentType}
            </span>
          ),
        },
        {
          label: "Valor",
          render: (item) => (
            <span className="fw-semibold">{formatMoneyBR(item.amount)}</span>
          ),
        },
        {
          label: "Status",
          render: (item) =>
            item.isActive ? (
              <span className="badge text-bg-success" style={{ fontSize: "0.7rem" }}>Ativo</span>
            ) : (
              <span className="badge text-bg-danger" style={{ fontSize: "0.7rem" }}>Inativo</span>
            ),
        },
      ]}
      formFields={[
        {
          name: "supplierId",
          label: "Fornecedor",
          type: "autocomplete",
          placeholder: "Buscar fornecedor",
          fetchOptions: fetchSuppliers,
          getIdFromItem: (item) => item.supplierId ?? null,
          getLabelFromItem: (item) => item.supplier?.entity?.name ?? "",
        },
        { name: "title", label: "Título", type: "text", required: true },
        { name: "description", label: "Descrição", type: "text" },
        {
          name: "startDate",
          label: "Data de Início",
          type: "date",
          required: true,
          displayValue: (value) => formatDateBR(String(value ?? "")),
        },
        {
          name: "endDate",
          label: "Data de Término",
          type: "date",
          required: true,
          displayValue: (value) => formatDateBR(String(value ?? "")),
        },
        {
          name: "paymentType",
          label: "Tipo de pagamento",
          type: "select",
          options: [
            { value: "ONCE", label: "Único" },
            { value: "MONTHLY", label: "Mensal" },
            { value: "YEARLY", label: "Anual" },
          ],
        },
        { name: "amount", label: "Valor (R$)", type: "text", required: true },
        {
          name: "billingStartsNextMonth",
          label: "Iniciar cobrança no mês seguinte",
          type: "checkbox",
        },
        {
          name: "indexType",
          label: "Índice de reajuste",
          type: "select",
          options: [
            { value: "NONE", label: "Sem índice" },
            { value: "IPCA", label: "IPCA" },
            { value: "IGPM", label: "IGPM" },
          ],
          displayValue: (value) => INDEX_LABELS[String(value ?? "NONE")] ?? String(value),
        },
        { name: "adjustmentMonth", label: "Mês de ajuste", type: "text" },
        { name: "isActive", label: "Ativo", type: "checkbox" },
      ]}
      initialValues={{
        supplierId: null,
        title: "",
        description: "",
        startDate: "",
        endDate: "",
        paymentType: "MONTHLY",
        amount: "",
        indexType: "NONE",
        adjustmentMonth: "",
        billingStartsNextMonth: false,
        isActive: true,
      }}
      mapToPayload={(values) => ({
        supplierId: values.supplierId ? Number(values.supplierId) : null,
        title: values.title,
        description: values.description || null,
        startDate: toDateInput(values.startDate),
        endDate: toDateInput(values.endDate),
        paymentType: values.paymentType,
        amount: Number(values.amount),
        indexType: values.indexType || "NONE",
        adjustmentMonth: values.adjustmentMonth ? Number(values.adjustmentMonth) : null,
        billingStartsNextMonth: values.billingStartsNextMonth,
        isActive: values.isActive,
      })}
      permissionWrite="FINANCE_WRITE"
      renderFormExtras={({ values, setValues, setError, editingId, readOnly, refresh }) => (
        <ContractFormExtras
          values={values}
          setValues={setValues}
          setError={setError}
          editingId={editingId}
          readOnly={readOnly}
          refresh={refresh}
          onGenerate={async (contractId) => {
            const response = await client.post(
              `/modules/contracts/${contractId}/generate`,
              {}
            );
            return response.ok;
          }}
        />
      )}
    />
  );
};
